import React, { useCallback, useEffect, useState } from "react";
import CommonFormLabel from "../../../../components/common-form-label/index";
import Input from "../../../../components/input/index";
import SelectBox from "../../../../components/select-box/index";
import axiosApi from "../../../../utilities/axiosApi";
import { useDispatch, useSelector } from "react-redux";
import { fetchEvents } from "../../../../store/eventsSlice";
import {
  DocumentAttachmentIcon,
  TextFontIcon,
  FileDownloadIcon,
  SmartPhone04Icon,
  CheckmarkBadge04Icon,
} from "../../../../icons/icons";

/**
 * One choice card. Whole card is the hit area - a radio you have to aim at is
 * the main reason the old four-option row was easy to get wrong.
 */
function RouteCard({ selected, disabled, onSelect, icon, title, rate, children }) {
  return (
    <div
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onSelect()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onSelect();
        }
      }}
      className={`campaign-route-card d-flex gap-3 p-3 rounded-2 border ${
        selected ? "border-2 campaign-route-card-selected" : ""
      } ${disabled ? "opacity-50" : "cursor-pointer"}`}
    >
      <div className="campaign-route-card-icon d-flex align-items-start pt-1">
        {icon}
      </div>
      <div className="flex-grow-1">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <p className="fw-medium mb-0">{title}</p>
          {rate !== null && rate !== undefined && (
            <span className="campaign-route-card-rate rounded-1 px-2">
              {rate} {Number(rate) === 1 ? "credit" : "credits"} per guest
            </span>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}

export default function CampaignsType({
  campaingDetails,
  initialCampaignState,
  setInitialCampaignState,
  clientFromQuery,
}) {
  const dispatch = useDispatch();

  // Every client, connected or not. This used to filter to clients with a live
  // WhatsApp socket, so a customer whose number had dropped simply could not
  // find them in the list and was told nothing. The connection only matters
  // for one of the three routes, and it is called out below when it does.
  const customers = useSelector((state) => state.events?.events || []);

  const authId = useSelector((state) => state.auth.user.id);

  // Every rate quoted here comes from the API, so a card can never advertise a
  // price finalise would not charge.
  const [rates, setRates] = useState(null);

  const fetchCustomers = useCallback(() => {
    dispatch(fetchEvents({ id: authId }));
  }, [authId, dispatch]);

  useEffect(() => {
    if (campaingDetails) {
      fetchCustomers();
    }
  }, [campaingDetails, fetchCustomers]);

  useEffect(() => {
    let active = true;

    axiosApi
      .get("/api/customer/sender-options")
      .then((res) => {
        if (active && res?.data?.success) setRates(res.data.data);
      })
      .catch(() => {
        // Never block the builder over a price lookup. Without it the cards
        // simply carry no rate badge.
        if (active) setRates(null);
      });

    return () => {
      active = false;
    };
  }, []);

  // The route cannot change once the campaign has content built against it:
  // the steps, the billing rate and the sender all follow from it.
  const locked = Boolean(campaingDetails && campaingDetails?.template_type);

  const route = initialCampaignState.deliveryRoute || "personal";
  const isSelfServe = route === "self";

  const selectedCustomer = customers.find(
    (c) => c.id === initialCampaignState?.customer
  );

  // Only the customer's own number needs a connected phone. Our number and the
  // self-serve route do not touch their WhatsApp session at all, which is
  // exactly what makes them the answer when a number will not connect.
  const needsConnectedNumber = route === "personal";
  const numberIsDown =
    needsConnectedNumber &&
    selectedCustomer &&
    !selectedCustomer?.whatsapp_client?.is_active;

  const pick = (changes) =>
    setInitialCampaignState({ ...initialCampaignState, ...changes });

  return (
    <div className="bg-white rounded-2 overflow-hidden common-shadow mb-4">
      <div className="campaigns-type-content">
        <div className="campaigns-type-header mb-4">
          <h5 className="fw-medium mb-1">Create Campaign</h5>
          <p>Choose how your invitations reach your guests</p>
        </div>

        <div className="grid mb-4">
          <div className="g-col-5">
            <CommonFormLabel title="Select Customer" />
            <SelectBox
              name="customer"
              value={initialCampaignState?.customer}
              staticOption="Choose a Select Customer"
              data={customers.map((customer) => ({
                value: customer.id,
                label: customer?.whatsapp_client?.is_active
                  ? customer.name
                  : `${customer.name} — WhatsApp not connected`,
              }))}
              disabled={!!campaingDetails?.client_event?.id || !!clientFromQuery}
              onChange={(e) => pick({ customer: e.target.value })}
            />
          </div>
        </div>

        <div className="mb-4" role="radiogroup" aria-label="How invitations reach your guests">
          <CommonFormLabel title="How should these invitations reach your guests?" />

          <div className="d-flex flex-column gap-2 pt-1">
            <RouteCard
              selected={route === "personal"}
              disabled={locked}
              onSelect={() => pick({ deliveryRoute: "personal" })}
              icon={<SmartPhone04Icon width={24} height={24} color="#A5596A" strokeWidth="1.8" />}
              title="From my own WhatsApp number"
              rate={rates?.personal_credit_rate}
            >
              <p className="text-muted mb-0">
                Guests see your number. Connect it once by scanning a QR code.
                Word your message however you like.
              </p>
            </RouteCard>

            <RouteCard
              selected={route === "official"}
              disabled={locked}
              onSelect={() => pick({ deliveryRoute: "official" })}
              icon={<CheckmarkBadge04Icon width={24} height={24} color="#A5596A" strokeWidth="1.8" />}
              title="From our official WhatsApp number"
              rate={rates?.official_credit_rate}
            >
              <p className="text-muted mb-0">
                No QR code, and steadier than a personal number. Wording comes
                from a pre-approved template. Invitations, images and plain
                messages &mdash; video goes from your own number.
              </p>
            </RouteCard>

            <RouteCard
              selected={isSelfServe}
              disabled={locked}
              onSelect={() => pick({ deliveryRoute: "self" })}
              icon={<FileDownloadIcon width={24} height={24} color="#A5596A" strokeWidth="1.8" />}
              title="PDF download only"
              rate={rates?.pdf_only_credit_rate}
            >
              <p className="text-muted mb-0">
                We build a personalised invitation for every guest and you take
                the files. Nothing is sent to WhatsApp, and you can download
                them again any time for free.
              </p>
            </RouteCard>
          </div>
        </div>

        {/* The way out when a number will not connect. Says which alternatives
            exist and switches to them on the spot, because the customer cannot
            be expected to know that two of the three routes do not need their
            phone at all. Not a hard block: they may be scanning the QR code in
            another tab, and nothing is actually sent until finalise. */}
        {numberIsDown && (
          <div className="campaign-route-warning rounded-2 p-3 mb-4">
            <p className="fw-medium mb-1">
              {selectedCustomer?.name}'s WhatsApp number is not connected
            </p>
            <p className="mb-2">
              Nothing will send from it until it is. Scan the QR code on the
              client's page to connect it &mdash; or run this campaign a way
              that does not need their phone:
            </p>
            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className="campaign-route-warning-action rounded-1 px-3 py-1"
                onClick={() => pick({ deliveryRoute: "official" })}
              >
                Send from our number instead
              </button>
              <button
                type="button"
                className="campaign-route-warning-action rounded-1 px-3 py-1"
                onClick={() => pick({ deliveryRoute: "self" })}
              >
                Just give me the files
              </button>
            </div>
          </div>
        )}

        {/* Skipped for the self-serve route: there would be nothing to download
            but a document, so the answer is not the customer's to give. */}
        {!isSelfServe && (
          <div className="mb-4" role="radiogroup" aria-label="What you are sending">
            <CommonFormLabel title="What are you sending?" />
            <div className="d-flex flex-column flex-md-row gap-2 pt-1">
              <RouteCard
                selected={initialCampaignState.contentType !== "text"}
                disabled={locked}
                onSelect={() => pick({ contentType: "document" })}
                icon={<DocumentAttachmentIcon width={24} height={24} color="#A5596A" strokeWidth="1.8" />}
                title="A personalised invitation"
                rate={null}
              >
                <p className="text-muted mb-0">A document with each guest's name on it.</p>
              </RouteCard>

              <RouteCard
                selected={initialCampaignState.contentType === "text"}
                disabled={locked}
                onSelect={() => pick({ contentType: "text" })}
                icon={<TextFontIcon width={24} height={24} color="#A5596A" strokeWidth="1.8" />}
                title="Just a text message"
                rate={null}
              >
                <p className="text-muted mb-0">No attachment, message only.</p>
              </RouteCard>
            </div>
          </div>
        )}

        <div className="grid mb-4">
          <div className="g-col-5">
            <CommonFormLabel title="Campaigns Name" />
            <Input
              name="name"
              value={initialCampaignState.name}
              type="text"
              placeholder="Enter Campaigns Name"
              onChange={(e) => pick({ name: e.target.value })}
              className={`shadow-none`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
